import { motion } from 'framer-motion';
import { fadeUp, stagger, viewport } from '../../utils/animations';

const JOBS = [
  {
    company: 'Quber Pty Ltd',
    role: 'Full-Stack Developer',
    period: '12 Aug 2024 — 15 January 2026',
    location: 'Brisbane, Australia (Remote)',
    type: 'Full-time',
    color: '#6366f1',
    achievements: [
      'Delivered end-to-end features across multiple client projects, working on front end + supporting backend tasks.',
      'Built responsive, accessible UIs using React, Next.js, TypeScript, MUI,Tailwind,Tamagui, NativeWind, ensuring cross-browser and cross-device compatibility.',
      'Developed and integrated backend APIs/data flows, handled debugging, and solved complex issues through strong problem-solving.',
      'Worked with AWS, docker for deploying and maintaining both frontend and backend components.',
      'Created and maintained internal web/mobile apps for an Australian income tax lawyer and shipped multiple React Native applications.',
      'Contributed to a DIY app platform that lets users create their own mobile apps; quickly learned and applied new technologies as needed.',
      'Improved application performance and stability through optimization, troubleshooting, and refactoring.',
      'Shipped production-ready features across several concurrent projects with consistent quality.',
    ],
    tech: ['React', 'Next.js', 'TypeScript', 'Node.js', 'MongoDB', 'AWS', 'Docker', 'MUI', 'Tailwind', 'Tamagui', 'NativeWind', 'React Native', '+ more'],
  },
  {
    company: 'GATES APAC Pte. Ltd',
    role: 'Front-End Developer',
    period: '13 Jan 2022 — 10 May 2024',
    location: 'Singapore (Remote)',
    type: 'Full-time',
    color: '#8b5cf6',
    achievements: [
      'Built and maintained 8+ enterprise Angular applications serving 20,000+ users',
      'Led front -end development for Angular-based platforms: built, maintained, and improved complete websites, business portals, and internal tools.',
      'Delivered multiple government websites, including contributing to a large top ASEAN website ( development + ongoing maintenance).',
      'Built multi-platform products from a single codebase (Web + Android + iOS) using Ionic + Angular for fast setup, clean structure, and reusable components.',
      'Owned day-to-day delivery through GitLab: managed main branches, code reviews, and task -based development workflow.',
      'Improved UI quality and maintainability by refactoring, optimizing performance, and creating reusable UI components.',
      'Collaborated with designers/teams to implement UX updates and ensure consistent, scalable front -end architecture.',
      'Shipped and maintained high-traffic public websites with strong performance and long-term maintainability.',
      'Reduced UI complexity by introducing reusable component patterns and cleaner Angular architecture.',
      'Supported web + mobile releases efficiently using Ionic’s shared component approach.',
    ],
    tech: ['Ionic', 'Angular', 'TypeScript', 'SCSS','Bootstrap', 'NgRx', 'GitLab','Typescript', 'Figma', 'CI/CD', '+ more'],
  },
];

export default function Experience() {
  return (
    <section id="experience" className="relative py-28 lg:py-36 overflow-hidden">
      {/* Faint gradient wash */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 70% 60% at 50% 50%, rgba(99,102,241,0.04) 0%, transparent 70%)' }} />

      <div className="max-w-7xl mx-auto px-6 sm:px-8">

        {/* Header */}
        <motion.div variants={stagger(0.12)} initial="hidden" whileInView="show" viewport={viewport} className="mb-16">
          <motion.div variants={fadeUp} className="section-label">
            <span className="font-mono text-xs text-indigo-400 font-semibold uppercase tracking-[0.18em]">02 / Experience</span>
          </motion.div>
          <motion.h2 variants={fadeUp} className="font-display font-extrabold text-4xl sm:text-5xl text-white tracking-tight">
            Where I've <span className="gradient-text">worked</span>
          </motion.h2>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-indigo-500/40 via-violet-500/20 to-transparent hidden md:block" />

          <div className="space-y-8">
            {JOBS.map((job, i) => (
              <motion.div
                key={job.company}
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={viewport}
                transition={{ delay: i * 0.15, duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
                className="relative md:pl-10 group"
              >
                {/* Timeline dot */}
                <div className="hidden md:block">
                  <div
                    className="absolute left-[-5px] top-7 w-2.5 h-2.5 rounded-full border-2 border-ink-950"
                    style={{ background: job.color }}
                  />
                  <motion.div
                    className="absolute left-[-9px] top-3 w-[18px] h-[18px] rounded-full"
                    style={{ background: job.color, opacity: 0.15 }}
                    animate={{ scale: [1, 1.5, 1], opacity: [0.15, 0.05, 0.15] }}
                    transition={{ duration: 3, repeat: Infinity, delay: i * 1 }}
                  />
                </div>

                {/* Card */}
                <motion.div
                  whileHover={{ y: -4 }}
                  transition={{ duration: 0.25 }}
                  className="glass glass-hover rounded-3xl p-7 sm:p-8 border border-white/[0.06]"
                >
                  {/* Header row */}
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
                    <div>
                      <h3 className="font-display font-bold text-xl sm:text-2xl text-white">{job.role}</h3>
                      <p className="text-base font-semibold mt-0.5" style={{ color: job.color }}>{job.company}</p>
                    </div>
                    <div className="flex flex-col items-start sm:items-end gap-1 flex-shrink-0">
                      <span className="font-mono text-xs text-zinc-400 font-medium px-3 py-1 glass rounded-full border border-white/[0.06]">
                        {job.period}
                      </span>
                      <span className="text-xs text-zinc-600">{job.location}</span>
                    </div>
                  </div>

                  {/* Achievements */}
                  <ul className="space-y-2.5 mb-6">
                    {job.achievements.map((a, j) => (
                      <motion.li
                        key={j}
                        initial={{ opacity: 0, x: -12 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={viewport}
                        transition={{ delay: i * 0.1 + j * 0.06, duration: 0.4 }}
                        className="flex items-start gap-3 text-sm text-zinc-400 leading-relaxed"
                      >
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: job.color }} />
                        {a}
                      </motion.li>
                    ))}
                  </ul>

                  {/* Tech tags */}
                  <div className="flex flex-wrap gap-2">
                    {job.tech.map(t => (
                      <span key={t} className="tag">{t}</span>
                    ))}
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
